'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/_lib/utils'

/**
 * Props for the shared previous/next week control strip.
 *
 * @property weekRange - Human-readable range label (e.g. `d. M. yyyy – d. M. yyyy`).
 * @property onPrevWeek - Moves the calendar one week backward.
 * @property onNextWeek - Moves the calendar one week forward.
 * @property className - Optional classes merged onto the flex row.
 */
export interface AProductTemplateCalendarWeekNavProps {
	weekRange: string
	onPrevWeek: () => void
	onNextWeek: () => void
	className?: string
}

const navCircleClass = cn(
	'flex size-10 shrink-0 items-center justify-center rounded-full',
	'bg-medante-purple-dark text-white shadow-sm',
	'transition-colors hover:bg-medante-purple-dark/50',
	'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-medante-purple-dark/35'
)

/**
 * Previous/next week buttons with a centered week-range label (mobile and desktop calendars).
 *
 * @param props - See {@link AProductTemplateCalendarWeekNavProps}.
 */
export function AProductTemplateCalendarWeekNav({
	weekRange,
	onPrevWeek,
	onNextWeek,
	className
}: AProductTemplateCalendarWeekNavProps) {
	return (
		<div className={cn('flex items-center justify-between gap-2 sm:gap-3', className)}>
			<button
				type='button'
				onClick={onPrevWeek}
				className={navCircleClass}
				aria-label='Predošlý týždeň'
			>
				<ChevronLeft className='size-5' strokeWidth={2.25} aria-hidden />
			</button>
			<p className='min-w-0 flex-1 text-center text-sm leading-tight font-medium text-medante-purple-dark'>
				{weekRange}
			</p>
			<button
				type='button'
				onClick={onNextWeek}
				className={navCircleClass}
				aria-label='Ďalší týždeň'
			>
				<ChevronRight className='size-5' strokeWidth={2.25} aria-hidden />
			</button>
		</div>
	)
}
